import { cloneDeep } from "lodash";
import axios from 'axios'
import { API_HOST } from "../../../config/app-const";
import {
  getNextPageUrl,
  pagination as paginationConstant,
} from "../../../helpers/store";

import { findActionTarget } from "../../../config/query-rules";

const filtersConstant = {
  sort: 'created_at',
  order: 'asc',
  name: '',
}

const actionField = {
  value: "",
  children: [],
}

const state = {
  actions: [],
  actionOptions: {
    isBusy: false,
    data: [],
    error: null
  },
}

const mutations = {

  SET_RULES_ACTIONS_BUSY(state, payload) {
    state.actionOptions.isBusy = payload;
  },

  FETCH_RULES_ACTIONS_SUCCESS(state, payload) {
    state.actionOptions.data = payload
  },

  FETCH_RULES_ACTIONS_FAIL(state, payload) {
    state.actionOptions.error = payload;
    state.actionOptions.data = []
  },

  FETCH_SUB_ACTIONS_SUCCESS(state, { data, pagination, filters,  action, actionIndex, childIndex = 0 }) {
    if(childIndex === -1) childIndex = 0

    // get our target action
    const target = findActionTarget(state, actionIndex, action)

    if(target) {
      const child = target.children[childIndex];
      if(!child) {
        target.children.push({
          type: action.searchable ? 'select' : 'text',
          value: null,
          searchable: action.searchable,
          isBusy: true,
          isInitiated: true,
          data,
          pagination: {
            page: pagination.currentPage,
            total_pages: pagination.totalPages,
            per_page: pagination.perPage || paginationConstant.per_page,
            total_count: pagination.totalCount
          },
          filters
        })
      } else {
        const currentDataValues = child.data.map(item => item.value)
        // push only new items, skip those which were preserved
        data.forEach(item => {
          if(!currentDataValues.includes(item.value)) child.data.push(item)
        })
        child.pagination = {
          page: pagination.currentPage,
          total_pages: pagination.totalPages,
          per_page: pagination.perPage || paginationConstant.per_page,
          total_count: pagination.totalCount
        }
        child.filters = filters;
      }
    }
  },

  RESET_SUB_ACTION_DATA(state, { action, actionIndex, childIndex = 0 }) {
    const target = findActionTarget(state, actionIndex, action)
    // if select is multiple, preserve selected items
    const values = action.parameter.type === 'Array' ? target.children[childIndex].value : []
    if(target) target.children[childIndex].data = target.children[childIndex].data.filter(item => values.includes(item.value));
  },

  RESET_SUB_ACTION_PAGINATION(state, { action, actionIndex, childIndex = 0 }) {
    const target = findActionTarget(state, actionIndex, action)
    if(target) target.children[childIndex].pagination =cloneDeep(paginationConstant)
  },

  SET_SUB_ACTIONS_VALUE(state, { value, action, actionIndex, childIndex }) {
    const target = findActionTarget(state, actionIndex, action)
    if(target && target.children[childIndex]) {
      target.children[childIndex].value = value;
    }
  },

  SET_SUB_ACTIONS_BUSY(state, {actionIndex= 0, childIndex = 0, value = true}) {
    if(!state.actions[actionIndex]) return
    if(!state.actions[actionIndex].children) return
    if(!state.actions[actionIndex].children[childIndex]) return
    state.actions[actionIndex].children[childIndex].isBusy = value;
  },

  SET_SUB_ACTIONS_QUERY(state, {action, actionIndex, childIndex = 0, value }) {
    const target = findActionTarget(state, actionIndex, action)
    if(target && target.children[childIndex]) {
      const { filters = {} } = target.children[childIndex]
      filters.name = value;
    }
  },

  SET_SUB_ACTIONS_INITIATED(state, {action, actionIndex, childIndex = 0, value = true}) {
    const target = findActionTarget(state, actionIndex, action)
    if(target && target.children[childIndex]) {
      target.children[childIndex].isInitiated = value;
    }
  },

  RESET_ACTIONS(state) {
    state.actions = []
  },

  ADD_BLANK_ACTION(state) {
    state.actions.push(cloneDeep(actionField))
  },

  SELECT_ACTION(state, {actionIndex, option}) {
    if(state.actions[actionIndex] && state.actions[actionIndex].children) state.actions[actionIndex].children = [] // reset children
    state.actions.splice(actionIndex, 1, {...state.actions[actionIndex], ...option})
  },

  REMOVE_ACTION(state, index) {
    state.actions.splice(index, 1)
  },

  REMOVE_SUB_ACTION(state, {action, actionIndex, childIndex}) {
    const target = findActionTarget(state, actionIndex, action)
    if(target) {
      target.children[childIndex].value = null
      target.children.splice(childIndex + 1)
    }
  },

  // text type has to be set manually on "selectAction"
  ADD_TEXT_TYPE_CHILD(state, { action, actionIndex }) {
    const target = findActionTarget(state, actionIndex, action)
    if(!target) return
    target.children = []
    if(target) {
      if (target.type === "setImpression") {
        target.children.push({
          type: 'number',
          value: '',
          isInitiated: true,
        });
      } else {
        target.children.push({
          type: 'text',
          value: '',
          isInitiated: true,
        });
      }
    }
  }
}

const actions = {

  getActionOptions({ commit, rootState }, projectUid) {

    return new Promise((resolve, reject) => {

      commit('SET_RULES_ACTIONS_BUSY', true)
      let url = `${API_HOST}/analysis/${projectUid}/rules/available/actions`

      const { accessToken } = rootState.user.user.data;
      const config = {
        headers: { Authorization: `Token token=${accessToken}` }
      };

      axios.get(url, config)
        .then(({data}) => {
          commit("FETCH_RULES_ACTIONS_SUCCESS", data)
          resolve(data)
        })
        .catch((e) => reject(e))
        .finally(() => {
          commit("SET_RULES_ACTIONS_BUSY", false)
        })
    })
  },

  getSubActions({ commit, rootState }, { projectUid, action, endpoint, actionIndex = null, childIndex = 0, query = '' }) {
    return new Promise((resolve, reject) => {
      actionIndex = actionIndex ? actionIndex : state.actions.findIndex(item => item.type === action.type)
      const subAction = action.children[childIndex]

      if(subAction && subAction.isBusy) return resolve('subAction busy')

      if(subAction && subAction.filters && subAction.filters.name !== query) {
        commit('RESET_SUB_ACTION_DATA', {action, actionIndex, childIndex})
        commit('RESET_SUB_ACTION_PAGINATION', {action, actionIndex, childIndex})
        commit('SET_SUB_ACTIONS_INITIATED', {action, actionIndex, childIndex, value: false})
        commit('SET_SUB_ACTIONS_QUERY', {action, actionIndex, childIndex, value: query})
      }

      const { pagination = {}, filters = {} } = extractPaginationAndFilters(subAction)
      if(isFullyLoaded(pagination)) return resolve()

      commit('SET_SUB_ACTIONS_BUSY', {actionIndex, childIndex, value: true})

      const { accessToken } = rootState.user.user.data;
      const config = { headers: { Authorization: `Token token=${accessToken}` } };
      const url = buildUrl(projectUid, endpoint, pagination, filters)

      axios.get(url, config)
        .then(({ data: { data } }) => {
          commit("FETCH_SUB_ACTIONS_SUCCESS", Object.assign(data, { action, filters, actionIndex, childIndex }))
          resolve()
        })
        .catch((e) => reject(e))
        .finally(() => {
          if(childIndex === -1) childIndex = 0 // if firstly created, childIndex is -1, but at this point we already have child
          commit('SET_SUB_ACTIONS_INITIATED', {action, childIndex, value: true})
          commit("SET_SUB_ACTIONS_BUSY", {actionIndex, childIndex, value: false})
        })
    })
  },
}

const buildUrl = (projectUid, endpoint, pagination, filters) => {
  const localPagination = { page: pagination.page, per_page: pagination.per_page }
  let url = `${API_HOST}/analysis/${projectUid}`
  url += getNextPageUrl(`/rules/available/actions/${endpoint}`, localPagination, filters)
  return url
}

const extractPaginationAndFilters = (subAction) => {
  let pagination = {}, filters = {}
  if(subAction) {
    pagination = subAction.pagination || {}
    filters = subAction.filters || {}
  } else {
    pagination = cloneDeep(paginationConstant)
    filters = cloneDeep(filtersConstant)
  }
  return { pagination, filters }
}

const isFullyLoaded = (pagination) => {
  return pagination.page && pagination.page >= pagination.total_pages
}

export default {
  namespaced: true,
  state,
  mutations,
  actions
};